import type { ReactNode } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { css } from '@compiled/react';

import Tabs, { Tab, TabList, useTabPanel } from '@atlaskit/tabs';
import { Text, Inline } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';

import { useAccessRequests } from '@confluence/access-requests/entry-points/useAccessRequests.tsx';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import type { Principal, PrincipalAndRole, Role } from '../../../commonTypes.tsx';
import {
	AccessRequestsDialogBody,
	type AccessRequestsDialogBodyProps,
} from '../accessRequests/AccessRequestsDialogBody.tsx';
import type { SpecificAccessDetailsProps } from '../specificAccessDetails/SpecificAccessDetails.tsx';

import { SpecificAccessPeopleTab } from './SpecificAccessPeopleTab.tsx';
import { createEmptySpecificAccessLiveDelta } from './specificAccessDialogLiveState.ts';
import { useLiveSpecificAccessDerivedState } from './liveSpecificAccessDerivedState.ts';
import { useLiveSpecificAccessActions } from './specificAccessDialogHooks.ts';
import { useUpdateContentDirectRestrictions } from './useUpdateContentDirectRestrictions.ts';
import { useSpecificAccessFlags } from './useSpecificAccessFlags.tsx';
import { useSpecificAccessLiveMutation } from './useSpecificAccessLiveMutation.ts';
import type { SpecificAccessLiveMutationArgs } from './specificAccessDialogTypes.ts';
const i18n = defineMessages({
	peopleWithAccessLabel: {
		id: 'share-and-restrict-dialog.specific-access-dialog-body.people-with-access',
		defaultMessage: 'People with access',
		description: 'Label for tab for people with access',
	},
	requestsLabel: {
		id: 'share-and-restrict-dialog.specific-access-dialog-body.requests',
		defaultMessage: 'Requests',
		description: 'Tab label showing pending access requests for content in the sharing dialog',
	},
});

export type SpecificAccessDialogBodyProps = {
	contentId: string;
	principalCounts: {
		countOfIndividuals: number;
		countOfTeams: number;
		countOfGroups: number;
	};
	specificAccessDetails?: SpecificAccessDetailsProps;
	accessRequestsDialogBody?: AccessRequestsDialogBodyProps;

	/**
	 * Explicit persistence mode so this module can run in live vs staged contexts.
	 */
	persistenceMode: 'live' | 'staged';

	/**
	 * Live-mode integration for the Specific Access dialog.
	 */
	liveMutationContext: {
		currentUser: Principal;
		/**
		 * Used for computing whether live mutations should include invites.
		 * TODO: includeInvites support is currently not implemented for the live dialog yet.
		 */
		principalsPendingAccess: PrincipalAndRole[];
	};

	/**
	 * Staged-mode integration for the Specific Access dialog (publish modal flow).
	 */
	stagedActionContext?: {
		removeAllSpecificAccessPrincipals: () => void;
	};
};

const customPanelStyles = css({
	alignItems: 'stretch',
	width: '100%',
});

const tabPanelContentStyles = css({
	flex: '1 1 auto',
	width: '100%',
	minWidth: 0,
});

type CustomTabPanelProps = { children: ReactNode };

const CustomTabPanel = ({ children }: CustomTabPanelProps) => {
	const tabPanelAttributes = useTabPanel();

	return (
		<div css={customPanelStyles} {...tabPanelAttributes}>
			<div css={tabPanelContentStyles}>{children}</div>
		</div>
	);
};

const SpecificAccessDialogBodyInternal = ({
	contentId,
	principalCounts,
	specificAccessDetails,
	accessRequestsDialogBody,
	persistenceMode,
	liveMutationContext,
	stagedActionContext,
	flags,
}: SpecificAccessDialogBodyProps & WithFlagsProps) => {
	const [tabSelection, setTabSelection] = useState(0);

	// NOTE: We intentionally keep this hook inside the dialog body module now that
	// tabs are owned here (not in the header). This preserves the “fresh count”
	// behavior using `cache-and-network`.
	//
	// If we later decide we need an explicit refetch-on-back behavior, we can
	// implement it here (pinned for now).
	const { requests } = useAccessRequests('cache-and-network', 'SpecificAccessDialogBody');

	const count = requests?.length ?? 0;

	const { currentUser, principalsPendingAccess } = liveMutationContext;

	const { cloudId } = useSessionData();
	const [mutateDirectRestrictions] = useUpdateContentDirectRestrictions();

	// --- Live delta state (dialog-owned) ---
	const [liveSpecificAccessDelta, setLiveSpecificAccessDelta] = useState(
		createEmptySpecificAccessLiveDelta,
	);
	const [savingPrincipalIds, setSavingPrincipalIds] = useState<Set<string>>(() => new Set());

	const { specificAccessPrincipalById } = useLiveSpecificAccessDerivedState({
		specificAccessDetails,
		currentUserId: currentUser.id,
		liveSpecificAccessDelta,
		principalsPendingAccess,
	});

	// TODO(share-and-restrict-dialog): Backend does not support includeInvites yet for this mutation.
	const includeInvites = false;

	const specificAccessFlags = useSpecificAccessFlags({ flags });

	const principalById = useMemo(() => {
		const map = new Map<string, { principal: Principal }>();
		for (const [id, entry] of specificAccessPrincipalById.entries()) {
			map.set(id, { principal: entry.principal });
		}
		return map;
	}, [specificAccessPrincipalById]);

	const { triggerLiveMutation: triggerLiveMutationInternal } = useSpecificAccessLiveMutation({
		cloudId,
		contentId,
		includeInvites,
		principalById,
		mutateDirectRestrictions,
		flags: specificAccessFlags,
	});

	const triggerLiveMutation = useCallback(
		async (args: SpecificAccessLiveMutationArgs) => {
			if (persistenceMode === 'staged') {
				return;
			}
			await triggerLiveMutationInternal(args);
		},
		[persistenceMode, triggerLiveMutationInternal],
	);

	const { onChangeRole, onRemovePrincipal, onRemoveAllPrincipals } = useLiveSpecificAccessActions({
		setLiveSpecificAccessDelta,
		setSavingPrincipalIds,
		triggerLiveMutation,
	});

	const getSpecificAccessDetails = useCallback((): SpecificAccessDetailsProps | undefined => {
		if (!specificAccessDetails) {
			return undefined;
		}

		if (persistenceMode === 'staged') {
			return specificAccessDetails;
		}

		// Live mode: use live mutation actions
		return {
			...specificAccessDetails,
			principals: (specificAccessDetails.principals ?? []).map((row) => {
				if (!row) {
					return row;
				}

				const principalId = row.principalDetails.principal.id;

				return {
					...row,
					principalAccess: {
						...row.principalAccess,
						access: row.principalDetails.role,
						isSaving: savingPrincipalIds.has(principalId),
						onChange: async (role: Role) =>
							onChangeRole(principalId, role, row.principalDetails.role),
						onRemove: async () => onRemovePrincipal(principalId),
					},
				};
			}),
		};
	}, [onChangeRole, onRemovePrincipal, persistenceMode, savingPrincipalIds, specificAccessDetails]);

	const onRemoveAll = useCallback(async (): Promise<void> => {
		if (persistenceMode === 'staged' && stagedActionContext) {
			stagedActionContext.removeAllSpecificAccessPrincipals();
		} else {
			await onRemoveAllPrincipals({
				currentUserId: currentUser.id,
				principalIds: Array.from(specificAccessPrincipalById.keys()),
			});
		}
	}, [
		currentUser.id,
		onRemoveAllPrincipals,
		persistenceMode,
		specificAccessPrincipalById,
		stagedActionContext,
	]);

	return (
		<Tabs onChange={setTabSelection} selected={tabSelection} id="controlled">
			<TabList>
				<Tab testId="specific-access-tab-people">
					<FormattedMessage {...i18n.peopleWithAccessLabel} />
				</Tab>
				<Tab testId="specific-access-tab-requests">
					<Inline space="space.075" alignBlock="center">
						<Text>
							<FormattedMessage {...i18n.requestsLabel} />
						</Text>
						{count ? (
							<Badge appearance="important" max={9}>
								{count}
							</Badge>
						) : null}
					</Inline>
				</Tab>
			</TabList>
			<CustomTabPanel>
				<SpecificAccessPeopleTab
					principalCounts={principalCounts}
					specificAccessDetails={getSpecificAccessDetails()}
					onRemoveAll={onRemoveAll}
				/>
			</CustomTabPanel>
			<CustomTabPanel>
				{accessRequestsDialogBody && <AccessRequestsDialogBody {...accessRequestsDialogBody} />}
			</CustomTabPanel>
		</Tabs>
	);
};

export const SpecificAccessDialogBody = withFlags(SpecificAccessDialogBodyInternal);
